import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-slate-900">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <div>
            <p className="text-sm text-slate-600 mb-6">Last Updated: January 1, 2025</p>
          </div>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">1. Introduction</h2>
            <p className="text-slate-700 leading-relaxed">
              SmartReqs ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">2. Information We Collect</h2>
            <p className="text-slate-700 leading-relaxed">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li><strong>Account Information:</strong> Email address, password (encrypted), and company name</li>
              <li><strong>Job Requisition Data:</strong> Job titles, descriptions, requirements, and related content you create</li>
              <li><strong>Usage Data:</strong> Information about how you use the Service, including features accessed and actions taken</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe; we do not store complete payment card details</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">3. How We Use Your Information</h2>
            <p className="text-slate-700 leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process your subscription payments and transactions</li>
              <li>Generate AI-powered job requisitions based on your input</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">4. Information Sharing and Disclosure</h2>
            <p className="text-slate-700 leading-relaxed">
              We do not sell your personal information. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., payment processing via Stripe, AI services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to respond to legal process</li>
              <li><strong>Protection of Rights:</strong> To protect the rights, property, or safety of SmartReqs, our users, or others</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">5. Data Security</h2>
            <p className="text-slate-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure password hashing using industry-standard algorithms</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">6. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">7. Your Rights and Choices</h2>
            <p className="text-slate-700 leading-relaxed">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Export:</strong> Request a copy of your data in a portable format</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              To exercise these rights, please contact us through the support options in your dashboard.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">8. Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our Service and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">9. Third-Party Services</h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service uses third-party services including:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700">
              <li><strong>Stripe:</strong> For payment processing (see Stripe's Privacy Policy)</li>
              <li><strong>AI Services:</strong> For content generation (data is processed according to service provider policies)</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              These third parties have their own privacy policies governing their collection and use of your information.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service is not intended for use by children under the age of 18. We do not knowingly collect personal information from children under 18. If you become aware that a child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">11. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country. We take steps to ensure that your information receives an adequate level of protection.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-slate-900">13. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at{" "}
              <a href="mailto:admin@smartreqs.ai" className="text-blue-600 hover:underline">
                admin@smartreqs.ai
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
